import { Component, OnInit, ViewChild, inject, input } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideLayers, lucideLock } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { TagifySettings, TagifyComponent } from 'ngx-tagify';
import { ToastrService } from 'ngx-toastr';
import { Client } from '../../../events/models/client.model';
import { DialogCloseReason } from '../../../layout/dialog/app-dialog.service';
import { ConfigurationService } from '../../../services/configuration.service';
import { UtilsService } from '../../../services/utils.service';
import { GroupLink } from '../../models/group-links.model';
import { GroupLinkService } from '../../services/group-links.service';
import {
  GsEventSelectorComponent,
  SelectionMeta,
} from '../../../../shared/features/event-selector/gs-event-selector.component';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.css'],
  imports: [
    BoDialogComponent,
    ButtonComponent,
    FormsModule,
    TagifyComponent,
    ReactiveFormsModule,
    GsEventSelectorComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideLayers, lucideLock })],
})
export class CreateComponent implements OnInit {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private groupLinkService = inject(GroupLinkService);
  private utils = inject(UtilsService);
  private toastr = inject(ToastrService);
  configurationService = inject(ConfigurationService);

  @ViewChild(GsEventSelectorComponent)
  eventSelector: GsEventSelectorComponent;

  readonly clientData = input.required<Client>();

  public item: Partial<GroupLink>;
  public selectedEventIds: number[] = [];
  public hashValidator = {
    name: false,
    description: false,
    defaultBuyerTypeName: false,
    defaultBuyerTypeId: false,
    salesRepId: false,
    maxTickets: false,
    minTickets: false,
    adaHoldCodes: false,
    regularHoldCodes: false,
    events: false,
    privateSellingWhitelists: false,
  };
  public currentStep = 1;
  public isAllEventsSelected = false;

  public form = new FormControl<any[]>([]);
  public adaHoldCodesForm = new FormControl<{ value: string }[]>([]);
  public regularHoldCodesForm = new FormControl<{ value: string }[]>([]);
  public settings: TagifySettings = {
    placeholder: 'Press enter to insert...',
    trim: true,
    duplicates: false,
    delimiters: /,|\s|\n/,
    pasteAsTags: true,
    keepInvalidTags: false,
    editTags: {
      clicks: 1,
    },
    pattern: /^[0-9]+$/,
    callbacks: {
      invalid: this.onInvalidTag.bind(this),
    },
  };

  get slug(): string {
    return '/' + this.utils.slugifyName(this.item?.name || '');
  }

  public copyToClipboard(): void {
    const url = this.configurationService.getPortalBaseUrl() + this.slug;

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.toastr.success(`URL copied to clipboard`);
        })
        .catch(err => {
          console.error('Clipboard error:', err);
          this.fallbackCopyToClipboard(url);
        });
    } else {
      this.fallbackCopyToClipboard(url);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.toastr.success(`URL copied to clipboard`);
      } else {
        this.toastr.error('Unable to copy to clipboard');
      }
    } catch (err) {
      this.toastr.error('Unable to copy to clipboard');
    }

    document.body.removeChild(textArea);
  }

  ngOnInit(): void {
    const clientData = this.clientData();
    this.item = {
      isActive: true,
      isPrivateSelling: false,
      name: '',
      slug: '',
      description: '',
      defaultBuyerTypeId: clientData.defaultBuyerTypeId,
      defaultBuyerTypeName: clientData.defaultBuyerTypeName,
      maxTickets: clientData.maxTickets,
      minTickets: clientData.minTickets,
      adaHoldCodes: clientData.adaHoldCodes,
      regularHoldCodes: clientData.regularHoldCodes,
      salesRepId: null,
    };

    if (this.item.adaHoldCodes) {
      this.adaHoldCodesForm.setValue(
        this.item.adaHoldCodes.map(code => ({ value: code.toString() }))
      );
    }
    if (this.item.regularHoldCodes) {
      this.regularHoldCodesForm.setValue(
        this.item.regularHoldCodes.map(code => ({ value: code.toString() }))
      );
    }
  }

  public onSelectAllEventsChange(event: any) {
    this.eventSelector.selectAll(event.target.checked);
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    if (this.currentStep === 1) {
      this.saveStep1();
    } else {
      this.saveStep2();
    }
  }

  private saveStep1() {
    this.item.adaHoldCodes = this.getHoldCodesData(this.adaHoldCodesForm);
    this.item.regularHoldCodes = this.getHoldCodesData(
      this.regularHoldCodesForm
    );

    let hasError = this.checkInputs(this.item);
    if (hasError) return;

    const body = {
      ...this.item,
      adaHoldCodes: this.item.adaHoldCodes,
      regularHoldCodes: this.item.regularHoldCodes,
      salesRepId: this.normalizeSalesRepId(this.item.salesRepId),
      events: this.selectedEventIds || [],
      privateSellingWhitelists: this.getWhitelistData(),
    };

    delete body.id;
    delete body.slug;
    delete body.clientId;
    delete body.client;
    delete body.createdAt;
    delete body.updatedAt;

    const { isPrivateSelling, privateSellingWhitelists, ...step1Body } = body;

    this.groupLinkService.create$(step1Body as any).subscribe({
      next: response => {
        this.item.id = response.data?.id;
        this.currentStep = 2;
      },
      error: err => {
        this.toastr.error(
          err.error?.error?.message ||
            err.error?.message ||
            err.message ||
            'An error occurred'
        );
        console.log(err);
      },
    });
  }

  private saveStep2() {
    this.hashValidator.privateSellingWhitelists = false;
    const privateSellingWhitelists = this.getWhitelistData();

    if (
      this.item.isPrivateSelling &&
      this.utils.isEmpty(privateSellingWhitelists)
    ) {
      this.hashValidator.privateSellingWhitelists = true;
      return;
    }

    const body = {
      isPrivateSelling: this.item.isPrivateSelling,
      privateSellingWhitelists,
    };

    this.groupLinkService.update$(this.item.id, body).subscribe({
      next: response => {
        this.onDismiss('success');
      },
      error: err => {
        this.toastr.error(
          err.error?.error?.message ||
            err.error?.message ||
            err.message ||
            'An error occurred'
        );
        console.log(err);
      },
    });
  }

  public onEventSelectionChange(ids: number[]) {
    this.selectedEventIds = ids;
  }

  public onEventSelectionMeta(meta: SelectionMeta) {
    this.isAllEventsSelected = meta.isAllSelected;
  }

  private getHoldCodesData(form: FormControl): string[] {
    const formValue = form.value || [];
    return formValue.map(item =>
      typeof item === 'string' ? item : item.value
    );
  }

  private onInvalidTag(event: any) {
    let message = '';
    if (event.detail.message === 'already exists') {
      message = 'This patron identificator already exists in your allowlist.';
    } else {
      message =
        'Invalid patron identificator format. Please make sure to insert a valid number.';
    }
    this.toastr.error(message);
  }

  private getWhitelistData() {
    const formValue = this.form.value || [];
    return formValue.map(({ value, __tagId }) => ({
      patronId: +value,
      tagId: __tagId as string,
    }));
  }

  private checkInputs(instance: Partial<GroupLink>): boolean {
    let hasErrors = false;
    Object.keys(this.hashValidator).forEach(value => {
      this.hashValidator[value] = false;
    });

    if (this.utils.isEmpty(instance.name)) {
      this.hashValidator.name = true;
      hasErrors = true;
    }
    if (
      !this.utils.onlyHasNumbers(instance.defaultBuyerTypeId) ||
      instance.defaultBuyerTypeId <= 0
    ) {
      this.hashValidator.defaultBuyerTypeId = true;
      hasErrors = true;
    }
    if (
      !this.utils.onlyHasNumbers(instance.maxTickets) ||
      instance.maxTickets < 1 ||
      instance.maxTickets <= instance.minTickets
    ) {
      this.hashValidator.maxTickets = true;
      hasErrors = true;
    }
    if (
      !this.utils.onlyHasNumbers(instance.minTickets) ||
      instance.minTickets < 0
    ) {
      this.hashValidator.minTickets = true;
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.adaHoldCodes)) {
      this.hashValidator.adaHoldCodes = true;
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.regularHoldCodes)) {
      this.hashValidator.regularHoldCodes = true;
      hasErrors = true;
    }
    if (this.utils.isEmpty(this.selectedEventIds || [])) {
      this.hashValidator.events = true;
      hasErrors = true;
    }
    if (this.isInvalidOptionalSalesRepId(instance.salesRepId)) {
      this.hashValidator.salesRepId = true;
      hasErrors = true;
    }

    return hasErrors;
  }

  private normalizeSalesRepId(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private isInvalidOptionalSalesRepId(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    return !this.utils.onlyHasNumbers(value) || Number(value) <= 0;
  }
}
